// Group Insurance — admin submissions panel: filter, search, view, manage
// status, request corrections, Excel export (all / filtered).
// Props: lockDistrict (district-role viewers are pinned to their district),
// readOnly (state/district viewers cannot change status or request corrections).
import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { PersonsSummary } from "@/components/insurance/RepeaterField";
import { calcAge } from "@/components/insurance/FieldRenderer";
import { Download, Eye, Loader2, RotateCcw, Send } from "lucide-react";

const STATUS_OPTIONS = ["submitted", "under_review", "data_verified", "correction_required", "sent_to_insurance", "completed"];
const STATUS_LABEL = (s) => (s || "").replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase());
const STATUS_CLS = {
  draft: "bg-slate-100 text-slate-700", submitted: "bg-blue-100 text-blue-800",
  under_review: "bg-amber-100 text-amber-800", data_verified: "bg-emerald-100 text-teal-700",
  correction_required: "bg-red-100 text-red-800", sent_to_insurance: "bg-indigo-100 text-indigo-800",
  completed: "bg-emerald-100 text-emerald-900",
};

const EMPTY_FILTERS = {
  district: "", local_body: "", kelta_id: "", name: "", status: "", has_insurance: "",
  insurance_type: "", parents_coverage: "", gender: "", age_min: "", age_max: "", date_from: "", date_to: "",
};

export default function SubmissionsPanel({ lockDistrict = "", readOnly = false }) {
  const baseFilters = lockDistrict ? { ...EMPTY_FILTERS, district: lockDistrict } : EMPTY_FILTERS;
  const [filters, setFilters] = useState(baseFilters);
  const [data, setData] = useState({ rows: [], total: 0, districts: [] });
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [correction, setCorrection] = useState("");
  const [busy, setBusy] = useState("");

  const load = useCallback((f = filters) => {
    setLoading(true);
    const effective = lockDistrict ? { ...f, district: lockDistrict } : f;
    const params = Object.fromEntries(Object.entries(effective).filter(([, v]) => v));
    api.get("/insurance/admin/submissions", { params })
      .then(r => setData(r.data))
      .catch(e => toast.error(e?.response?.data?.detail || "Load failed"))
      .finally(() => setLoading(false));
  }, [filters, lockDistrict]);

  useEffect(() => { load(baseFilters); }, []); // eslint-disable-line

  const setF = (k, v) => setFilters(f => ({ ...f, [k]: v }));

  const doExport = async (filtered) => {
    setBusy(filtered ? "filtered" : "all");
    try {
      const effective = lockDistrict ? { ...filters, district: lockDistrict } : filters;
      const params = filtered ? Object.fromEntries(Object.entries(effective).filter(([, v]) => v)) : (lockDistrict ? { district: lockDistrict } : {});
      const r = await api.get("/insurance/admin/export", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `KELTA_Group_Insurance_${filtered ? "filtered" : "all"}_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      toast.success("Excel exported");
    } catch { toast.error("Export failed"); }
    finally { setBusy(""); }
  };

  const openDetail = async (row) => {
    try {
      const { data } = await api.get(`/insurance/admin/submissions/${row.id}`);
      setDetail(data);
      setCorrection(data.correction_note || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not open submission"); }
  };

  const setStatus = async (st) => {
    try {
      const { data } = await api.patch(`/insurance/admin/submissions/${detail.id}/status`, { status: st });
      setDetail(data);
      toast.success(`Status → ${STATUS_LABEL(st)}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sendCorrection = async () => {
    if (!correction.trim()) { toast.error("Write what needs correction"); return; }
    try {
      const { data } = await api.post(`/insurance/admin/submissions/${detail.id}/request-correction`, { note: correction });
      setDetail(data);
      toast.success("Correction requested — the member can now edit & resubmit");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const sel = (k, placeholder, options) => (
    <Select value={filters[k] || "all"} onValueChange={v => setF(k, v === "all" ? "" : v)}>
      <SelectTrigger className="h-9 text-xs" data-testid={`gi-filter-${k}`}><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{placeholder}</SelectItem>
        {options.map(o => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );

  return (
    <div className="space-y-4" data-testid="gi-submissions">
      {/* filter bar */}
      <Card className="p-4 space-y-3">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2">
          {lockDistrict
            ? <Input value={lockDistrict} disabled className="h-9 text-xs font-semibold" data-testid="gi-filter-district" title="Locked to your district" />
            : sel("district", "All Districts", data.districts)}
          <Input placeholder="Local Body" value={filters.local_body} onChange={e => setF("local_body", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-local-body" />
          <Input placeholder="KELTA ID" value={filters.kelta_id} onChange={e => setF("kelta_id", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-kelta-id" />
          <Input placeholder="Member Name" value={filters.name} onChange={e => setF("name", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-name" />
          {sel("status", "All Statuses", STATUS_OPTIONS.map(STATUS_LABEL))}
          {sel("has_insurance", "Existing Insurance?", ["Yes", "No"])}
          {sel("insurance_type", "Insurance Type", ["Self / Individual Insurance", "Family Insurance", "Both Self and Family Insurance"])}
          {sel("parents_coverage", "Father/Mother Coverage", ["Both Father and Mother", "Father Only", "Mother Only", "Neither"])}
          {sel("gender", "Gender", ["Male", "Female", "Other"])}
          <div className="flex gap-1">
            <Input type="number" placeholder="Age ≥" value={filters.age_min} onChange={e => setF("age_min", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-age-min" />
            <Input type="number" placeholder="Age ≤" value={filters.age_max} onChange={e => setF("age_max", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-age-max" />
          </div>
          <Input type="date" value={filters.date_from} onChange={e => setF("date_from", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-date-from" title="Submitted from" />
          <Input type="date" value={filters.date_to} onChange={e => setF("date_to", e.target.value)} className="h-9 text-xs" data-testid="gi-filter-date-to" title="Submitted to" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button size="sm" onClick={() => load()} className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="gi-apply-filters">Apply Filters</Button>
          <Button size="sm" variant="outline" onClick={() => { setFilters(baseFilters); load(baseFilters); }} data-testid="gi-clear-filters"><RotateCcw className="w-3.5 h-3.5 mr-1" /> Clear</Button>
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={() => doExport(true)} disabled={!!busy} data-testid="gi-export-filtered">
            {busy === "filtered" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Export Filtered
          </Button>
          <Button size="sm" variant="outline" onClick={() => doExport(false)} disabled={!!busy} data-testid="gi-export-all">
            {busy === "all" ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />} Export All
          </Button>
        </div>
      </Card>

      {/* table */}
      <Card data-testid="gi-submissions-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Reference</TableHead><TableHead>KELTA ID</TableHead><TableHead>Member</TableHead>
              <TableHead>District</TableHead><TableHead>Local Body</TableHead><TableHead>Existing Ins.</TableHead>
              <TableHead className="text-center">Persons</TableHead><TableHead>Status</TableHead><TableHead>Submitted</TableHead><TableHead></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={10} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin inline" /></TableCell></TableRow>}
            {!loading && !data.rows.length && <TableRow><TableCell colSpan={10} className="text-center py-8 text-sm text-muted-foreground">No submissions match.</TableCell></TableRow>}
            {!loading && data.rows.map(r => (
              <TableRow key={r.id} className="cursor-pointer hover:bg-muted/40" onClick={() => openDetail(r)} data-testid={`gi-row-${r.ref_no}`}>
                <TableCell className="font-mono text-xs">{r.ref_no}</TableCell>
                <TableCell className="text-xs font-mono">{r.member?.kelta_id || "—"}</TableCell>
                <TableCell className="font-medium">{r.member?.name}</TableCell>
                <TableCell className="text-xs">{r.member?.district}</TableCell>
                <TableCell className="text-xs">{r.member?.local_body}</TableCell>
                <TableCell className="text-xs">{r.answers?.has_insurance || "—"}</TableCell>
                <TableCell className="text-center">{r.insured_count}</TableCell>
                <TableCell><Badge className={`${STATUS_CLS[r.status]} text-[10px]`}>{STATUS_LABEL(r.status)}</Badge></TableCell>
                <TableCell className="text-xs">{r.submitted_at ? new Date(r.submitted_at).toLocaleDateString() : "—"}</TableCell>
                <TableCell><Eye className="w-4 h-4 text-muted-foreground" /></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>

      {/* detail dialog */}
      <Dialog open={!!detail} onOpenChange={v => !v && setDetail(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="gi-detail-dialog">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  <span className="font-mono text-sm">{detail.ref_no}</span>
                  <Badge className={STATUS_CLS[detail.status]}>{STATUS_LABEL(detail.status)}</Badge>
                  <span className="text-xs font-normal text-muted-foreground">form v{detail.form_version}</span>
                </DialogTitle>
                <DialogDescription className="sr-only">Full submission details, insured persons and history</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 my-3">
                {[["KELTA ID", detail.member?.kelta_id], ["Name", detail.member?.name], ["Designation", detail.member?.designation],
                  ["District", detail.member?.district], ["Local Body", detail.member?.local_body], ["Mobile", detail.member?.mobile],
                  ["Email", detail.member?.email], ["DOB", detail.member?.dob], ["Gender", detail.member?.gender]].map(([k, v]) => (
                  <div key={k} className="rounded-md bg-muted/50 border border-border px-2.5 py-1.5">
                    <div className="text-[10px] uppercase tracking-wide text-muted-foreground">{k}</div>
                    <div className="text-xs font-semibold truncate">{v || "—"}</div>
                  </div>
                ))}
              </div>
              <h4 className="font-bold text-sm mt-2 mb-2">Answers</h4>
              <div className="rounded-lg border border-border divide-y divide-border text-sm" data-testid="gi-detail-answers">
                {(detail.schema_snapshot?.sections || []).filter(s => s.enabled !== false).flatMap(s => (s.fields || []).filter(f => !["heading", "description", "divider", "insured_repeater"].includes(f.type)).map(f => ({ f }))).map(({ f }) => {
                  const v = detail.answers?.[f.key];
                  if (v === undefined || v === "" || v === null || (Array.isArray(v) && !v.length)) return null;
                  const shown = f.type === "checkbox" ? (v ? "Yes" : "No")
                    : Array.isArray(v) ? (typeof v[0] === "string" ? v.join(", ") : `${v.length} row(s)`)
                    : String(v);
                  return (
                    <div key={f.id} className="px-3 py-2 grid sm:grid-cols-[1fr_1.2fr] gap-1">
                      <span className="text-muted-foreground text-xs">{f.label}</span>
                      <span className="text-xs font-medium">{shown}</span>
                    </div>
                  );
                })}
              </div>
              <h4 className="font-bold text-sm mt-4 mb-2">Insured Persons</h4>
              <PersonsSummary persons={detail.insured_persons} member={detail.member} testid="gi-detail-persons" />
              {detail.correction_note && (
                <div className="mt-3 rounded-md bg-red-50 border border-red-200 p-3 text-xs text-red-800">
                  <strong>Correction note:</strong> {detail.correction_note}
                </div>
              )}
              <h4 className="font-bold text-sm mt-4 mb-2">History</h4>
              <ul className="text-xs text-muted-foreground space-y-1" data-testid="gi-detail-history">
                {(detail.history || []).map((h, i) => (
                  <li key={i}>· {h.event} — {new Date(h.at).toLocaleString()}{h.by ? ` by ${h.by}` : ""}</li>
                ))}
              </ul>
              {!readOnly && (
                <div className="mt-4 flex flex-wrap items-end gap-2 border-t border-border pt-4">
                  <div className="space-y-1">
                    <Label className="text-xs">Set status</Label>
                    <Select value={detail.status} onValueChange={setStatus}>
                      <SelectTrigger className="w-56 h-9" data-testid="gi-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {STATUS_OPTIONS.map(s => <SelectItem key={s} value={s}>{STATUS_LABEL(s)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[240px] space-y-1">
                    <Label className="text-xs">Request correction from member</Label>
                    <div className="flex gap-2">
                      <Textarea rows={1} value={correction} onChange={e => setCorrection(e.target.value)} placeholder="What should the member correct?" className="text-xs" data-testid="gi-correction-note" />
                      <Button size="sm" variant="outline" onClick={sendCorrection} data-testid="gi-send-correction"><Send className="w-3.5 h-3.5 mr-1" /> Request</Button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
